/**
 * BrandConfig Component
 * Configures brand-related content generation settings
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Building2, Users, MousePointer, MapPin, MessageSquareOff } from 'lucide-react';
import type { BrandConfig as BrandConfigType, BrandFrequency, CompetitorPolicy, CtaStyle, CtaPlacement, FaqBrandComparison } from '@/hooks/useContentConfig';

interface BrandConfigProps {
  value: BrandConfigType;
  onChange: (value: BrandConfigType) => void;
  sourceLabel?: string;
  brandName?: string;
}

const BRAND_FREQUENCY_OPTIONS: { value: BrandFrequency; label: string; description: string }[] = [
  { value: 'minimal', label: 'Minimal', description: 'Once at most, only when necessary' },
  { value: 'natural', label: 'Natural', description: '2-3 times in longer content' },
  { value: 'emphasized', label: 'Emphasized', description: 'Prominent throughout' },
];

const COMPETITOR_POLICY_OPTIONS: { value: CompetitorPolicy; label: string; description: string }[] = [
  { value: 'never', label: 'Never Mention', description: 'Use generic terms like "alternatives"' },
  { value: 'compare_favorably', label: 'Compare Favorably', description: 'Mention competitors but position us better' },
  { value: 'neutral', label: 'Neutral Comparison', description: 'Factual, objective comparisons' },
];

const CTA_STYLE_OPTIONS: { value: CtaStyle; label: string; examples: string }[] = [
  { value: 'soft', label: 'Soft', examples: 'Learn more, Explore options, Discover' },
  { value: 'medium', label: 'Medium', examples: 'Shop now, View details, Get started' },
  { value: 'strong', label: 'Strong', examples: 'Buy now, Order today, Add to cart' },
];

const CTA_PLACEMENT_OPTIONS: { value: CtaPlacement; label: string; description: string }[] = [
  { value: 'end_only', label: 'End Only', description: 'At the end of content' },
  { value: 'natural', label: 'Natural', description: 'Where most helpful to reader' },
  { value: 'throughout', label: 'Throughout', description: 'Multiple CTAs in content' },
];

const FAQ_BRAND_COMPARISON_OPTIONS: { value: FaqBrandComparison; label: string; description: string }[] = [
  { value: 'enabled', label: 'Enabled', description: 'FAQs can include brand comparisons' },
  { value: 'disabled', label: 'Disabled', description: 'No brand comparisons in FAQs' },
];

export function BrandConfig({ value, onChange, sourceLabel, brandName }: BrandConfigProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-purple-500" />
            <CardTitle className="text-lg">Brand Elements</CardTitle>
          </div>
          {sourceLabel && (
            <Badge variant="outline" className="text-xs">
              From: {sourceLabel}
            </Badge>
          )}
        </div>
        <CardDescription>
          Control brand mentions, competitor references, and calls-to-action
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Brand Name Variant */}
        <div className="space-y-2">
          <Label htmlFor="brandNameVariant" className="text-sm font-medium">
            Brand Name Variant
          </Label>
          <Input
            id="brandNameVariant"
            placeholder={brandName || 'Enter preferred brand name'}
            value={value.brandNameVariant || ''}
            onChange={(e) => onChange({ ...value, brandNameVariant: e.target.value || undefined })}
          />
          <p className="text-xs text-muted-foreground">
            Leave empty to use the site name. Use this if you want a specific variation (e.g., "MyBrand" vs "My Brand Inc.")
          </p>
        </div>

        {/* Brand Frequency */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-medium">Brand Mention Frequency</Label>
          </div>
          <RadioGroup
            value={value.brandFrequency}
            onValueChange={(v) => onChange({ ...value, brandFrequency: v as BrandFrequency })}
            className="grid grid-cols-3 gap-3"
          >
            {BRAND_FREQUENCY_OPTIONS.map((opt) => (
              <div key={opt.value}>
                <RadioGroupItem
                  value={opt.value}
                  id={`brand-freq-${opt.value}`}
                  className="peer sr-only"
                />
                <Label
                  htmlFor={`brand-freq-${opt.value}`}
                  className="flex flex-col items-center gap-1 rounded-md border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary cursor-pointer text-center"
                >
                  <span className="font-medium">{opt.label}</span>
                  <span className="text-xs text-muted-foreground">{opt.description}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* Competitor Policy */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-medium">Competitor Mentions</Label>
          </div>
          <RadioGroup
            value={value.competitorPolicy}
            onValueChange={(v) => onChange({ ...value, competitorPolicy: v as CompetitorPolicy })}
            className="space-y-2"
          >
            {COMPETITOR_POLICY_OPTIONS.map((opt) => (
              <div key={opt.value} className="flex items-center space-x-3">
                <RadioGroupItem value={opt.value} id={`comp-${opt.value}`} />
                <Label htmlFor={`comp-${opt.value}`} className="flex flex-col cursor-pointer">
                  <span className="font-medium">{opt.label}</span>
                  <span className="text-xs text-muted-foreground">{opt.description}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* FAQ Brand Comparison */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <MessageSquareOff className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-medium">FAQ Brand Comparisons</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            Control whether FAQ sections include questions comparing your products with other brands
          </p>
          <RadioGroup
            value={value.faqBrandComparison}
            onValueChange={(v) => onChange({ ...value, faqBrandComparison: v as FaqBrandComparison })}
            className="grid grid-cols-2 gap-3"
          >
            {FAQ_BRAND_COMPARISON_OPTIONS.map((opt) => (
              <div key={opt.value}>
                <RadioGroupItem
                  value={opt.value}
                  id={`faq-comp-${opt.value}`}
                  className="peer sr-only"
                />
                <Label
                  htmlFor={`faq-comp-${opt.value}`}
                  className="flex flex-col items-center gap-1 rounded-md border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary cursor-pointer text-center"
                >
                  <span className="font-medium">{opt.label}</span>
                  <span className="text-xs text-muted-foreground">{opt.description}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* CTA Style */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <MousePointer className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-medium">Call-to-Action Style</Label>
          </div>
          <RadioGroup
            value={value.ctaStyle}
            onValueChange={(v) => onChange({ ...value, ctaStyle: v as CtaStyle })}
            className="grid grid-cols-3 gap-3"
          >
            {CTA_STYLE_OPTIONS.map((opt) => (
              <div key={opt.value}>
                <RadioGroupItem
                  value={opt.value}
                  id={`cta-style-${opt.value}`}
                  className="peer sr-only"
                />
                <Label
                  htmlFor={`cta-style-${opt.value}`}
                  className="flex flex-col items-start gap-1 rounded-md border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary cursor-pointer"
                >
                  <span className="font-medium">{opt.label}</span>
                  <span className="text-xs text-muted-foreground italic">{opt.examples}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* CTA Placement */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-medium">CTA Placement</Label>
          </div>
          <RadioGroup
            value={value.ctaPlacement}
            onValueChange={(v) => onChange({ ...value, ctaPlacement: v as CtaPlacement })}
            className="grid grid-cols-3 gap-3"
          >
            {CTA_PLACEMENT_OPTIONS.map((opt) => (
              <div key={opt.value}>
                <RadioGroupItem
                  value={opt.value}
                  id={`cta-place-${opt.value}`}
                  className="peer sr-only"
                />
                <Label
                  htmlFor={`cta-place-${opt.value}`}
                  className="flex flex-col items-center gap-1 rounded-md border-2 border-muted bg-popover p-3 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary cursor-pointer text-center"
                >
                  <span className="font-medium">{opt.label}</span>
                  <span className="text-xs text-muted-foreground">{opt.description}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      </CardContent>
    </Card>
  );
}
